// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewCommentsList.test.tsx
import { type AskPunttReviewComment } from "@mg/schemas/src/christo/askPunttReview";
import { CaretDown } from "@phosphor-icons/react";
import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { resolveEmbeddedCitationView } from "./resolveEmbeddedCitationView";

import {
  AssetViewerPane,
  type AssetFile,
  type AssetViewerPaneHandle,
} from "../../routes/ask-puntt/components/AssetViewerPane";
import {
  SpreadsheetCitationContextPanel,
  type SpreadsheetCitationSelection,
} from "../../routes/ask-puntt/components/SpreadsheetCitationContextPanel";
import {
  citationLabel,
  isFileCitation,
  isTextCitation,
} from "../../routes/ask-puntt/utils/commentSourceUtils";

type AskPunttReviewCommentSourceProps = {
  comment: AskPunttReviewComment;
  contextFiles: readonly AssetFile[];
  reviewFiles?: readonly AssetFile[];
};

function resolveSpreadsheetSource(
  comment: AskPunttReviewComment,
  contextFiles: readonly AssetFile[],
): { kind: "spreadsheet"; citation: SpreadsheetCitationSelection } | null {
  const citations = comment.citations ?? [];
  let sourceUrl: string | undefined;

  for (const citation of citations) {
    if (isFileCitation(citation) && citation.url) {
      sourceUrl = citation.url;
      break;
    }
    if (
      typeof citation === "object" &&
      citation !== null &&
      "sourceUrl" in citation &&
      typeof citation.sourceUrl === "string" &&
      citation.sourceUrl
    ) {
      sourceUrl = citation.sourceUrl;
      break;
    }
  }

  if (!comment.cellAnchor) return null;

  const resolvedUrl =
    sourceUrl ??
    contextFiles.find((file) =>
      citations.some(
        (citation) =>
          isFileCitation(citation) &&
          (citation.url === file.source ||
            citation.originalURL === file.source),
      ),
    )?.source ??
    contextFiles[0]?.source;

  if (!resolvedUrl) return null;

  return {
    kind: "spreadsheet",
    citation: {
      sourceUrl: resolvedUrl,
      cellAnchor: comment.cellAnchor,
      fileName: contextFiles.find((file) => file.source === resolvedUrl)?.name,
    },
  };
}

function sourceSummaryLabel(comment: AskPunttReviewComment): string {
  for (const citation of comment.citations ?? []) {
    if (isTextCitation(citation)) {
      return citationLabel(citation);
    }
    if (isFileCitation(citation)) {
      return citationLabel(citation);
    }
  }
  if (comment.cellAnchor?.sheetName) return comment.cellAnchor.sheetName;
  return "Source";
}

export function AskPunttReviewCommentSource({
  comment,
  contextFiles,
  reviewFiles = [],
}: AskPunttReviewCommentSourceProps) {
  const { t } = useTranslation("ui");
  const [sourceVisible, setSourceVisible] = useState(false);
  const sourcePaneRef = useRef<AssetViewerPaneHandle>(null);

  const spreadsheetSource = useMemo(
    () => resolveSpreadsheetSource(comment, contextFiles),
    [comment, contextFiles],
  );

  const embeddedView = useMemo(() => {
    if (spreadsheetSource) return null;
    return resolveEmbeddedCitationView(contextFiles, reviewFiles, comment);
  }, [comment, contextFiles, reviewFiles, spreadsheetSource]);

  useLayoutEffect(() => {
    if (!sourceVisible || !embeddedView) return;

    let cancelled = false;
    const flush = () => {
      if (cancelled) return;
      if (!sourcePaneRef.current) {
        requestAnimationFrame(flush);
        return;
      }
      sourcePaneRef.current.navigateToFile(
        embeddedView.initialAssetIndex,
        embeddedView.initialViewerPage,
      );
    };
    flush();

    return () => {
      cancelled = true;
    };
  }, [sourceVisible, embeddedView]);

  if (!spreadsheetSource && !embeddedView) return null;

  return (
    <div
      className="mt-3 border-l-2 border-puntt-neutral-gray-5 pl-3"
      data-testid={`ask-v2-review-comment-source-${comment._id}`}
    >
      <p className="text-[11px] font-medium uppercase tracking-wide text-puntt-neutral-gray-9">
        {sourceSummaryLabel(comment)}
        {comment.locationDescription ? (
          <span className="ml-1 normal-case text-puntt-neutral-gray-10">
            {comment.locationDescription}
          </span>
        ) : null}
      </p>
      <button
        type="button"
        className="mt-1 flex items-center gap-1 text-[12px] font-medium text-puntt-neutral-gray-11"
        data-testid={`ask-v2-review-comment-source-toggle-${comment._id}`}
        aria-expanded={sourceVisible}
        onClick={() => setSourceVisible((current) => !current)}
      >
        <CaretDown
          size={12}
          className={`transition-transform ${sourceVisible ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
        {sourceVisible
          ? t("askV2.review.hideSource")
          : t("askV2.review.showSource")}
      </button>
      {sourceVisible ? (
        <div
          className="mt-2 max-h-[220px] overflow-hidden rounded-md border border-puntt-neutral-gray-5 bg-base-white"
          data-testid={`ask-v2-review-comment-source-preview-${comment._id}`}
        >
          {spreadsheetSource ? (
            <SpreadsheetCitationContextPanel
              citation={spreadsheetSource.citation}
            />
          ) : embeddedView ? (
            <div className="h-[220px]">
              <AssetViewerPane
                key={`${comment._id}-${embeddedView.initialAssetIndex}-${embeddedView.initialViewerPage}`}
                ref={sourcePaneRef}
                title=""
                assets={embeddedView.sourceAssets}
                hideCommentPanel
                viewerToolbarPlacement="header"
                viewerContentInset="edge"
                initialAssetIndex={embeddedView.initialAssetIndex}
                initialViewerPage={embeddedView.initialViewerPage}
                highlightPosition={
                  sourceVisible ? embeddedView.highlightPosition : null
                }
                citationFocusPin={
                  sourceVisible ? embeddedView.citationFocusPin : null
                }
              />
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
